import "./TripHistoryComponent.scss";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import TripHistoryService from "../../services/TripHistoryService";
import { TripHistoryStats } from "../../interfaces/job.interface";
import SearchBar from "../SearchBar/SearchBar";
import DateTimePickerComponent from "../DateTimePicker/DateTimePicker";
import { transformTruckNumber } from "../../utils/general";
import { useLoader } from "../../contexts/LoaderContext";
import StatsBoxContainer from "../StatsBoxContainer/StatsBoxContainer";
import TableComponent from "../TableComponent/TableComponent";
import { SelectChangeEvent } from "@mui/material";
import { useLoading } from "../../contexts/LoadingContext";
import ContainerType from "../ContainerType/ContainerType";
import ContainerStatus from "../ContainerStatus/ContainerStatus";
import moment from "moment";

export default function TripHistoryComponent() {
  // State management
  const [trips, setTrips] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [tripStats, setTripStats] = useState<TripHistoryStats | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{
    startDate: number | null;
    endDate: number | null;
  }>({ startDate: null, endDate: null });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const initialLoadDone = useRef(false);

  // Refs
  const dateTimePickerStartRef = useRef<{ reset: () => void }>(null);
  const dateTimePickerEndRef = useRef<{ reset: () => void }>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const statsDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Context hooks
  const { showLoader, hideLoader } = useLoader();
  const { setLoading } = useLoading();

  // Memoized stats data
  const statsData = useMemo(
    () => [
      {
        name: "Total Trips",
        value: tripStats?.trips || 0,
        valueColor: "#59585c",
      },
      {
        name: "Total Containers",
        value: tripStats?.containers || 0,
        valueColor: "#59585c",
      },
      {
        name: "20ft",
        value: tripStats?.count20 || 0,
        valueColor: "#1d3557",
      },
      {
        name: "40ft",
        value: tripStats?.count40 || 0,
        valueColor: "#457b9d",
      },
      {
        name: "45ft",
        value: tripStats?.count45 || 0,
        valueColor: "#69b9bc",
      },
    ],
    [tripStats]
  );

  // Load trips data
  const loadTrips = useCallback(
    async (page: number, suppressLoader = false) => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      if (!initialLoadDone.current) {
        showLoader();
      } else if (!suppressLoader) {
        setLoading(true);
      }

      try {
        const response = await TripHistoryService.getTripHistory({
          limit: pageSize,
          skip: (page - 1) * pageSize,
          jobBreakupDetailId: searchQuery.trim() || undefined,
          from: dateRange.startDate || undefined,
          to: dateRange.endDate || undefined,
          signal: abortControllerRef.current.signal,
        });

        // If the request was aborted, don't process the response
        if (abortControllerRef.current.signal.aborted) {
          return;
        }

        if (!response?.data?.data?.content) {
          setTrips([]);
          setTotalPages(1);
          setTotalElements(0);
          return;
        }

        const newTrips = response.data.data.content;
        setTrips(newTrips);
        setTotalPages(response.data.data.page?.totalPages || 1);
        setTotalElements(response.data.data.page?.totalElements || 0);

        // Load stats on initial load or when there's a search query
        if (!initialLoadDone.current || searchQuery) {
          try {
            const statsResponse = await TripHistoryService.getTripHistoryStats({
              signal: abortControllerRef.current.signal,
            });
            if (!abortControllerRef.current.signal.aborted) {
              setTripStats(statsResponse.data);
            }
          } catch (error: any) {
            if (!error.name || error.name !== "AbortError") {
              setTripStats(null);
              console.error("Error loading trip history stats", error);
            }
          }
        }
      } catch (error: any) {
        // Only handle non-abort errors
        if (!error.name || error.name !== "AbortError") {
          console.error("Error loading trips", error);
          // Don't clear data on error, just log it
        }
      } finally {
        if (!initialLoadDone.current) {
          setTimeout(() => {
            hideLoader();
            setIsInitialLoading(false);
            initialLoadDone.current = true;
          }, 1000);
        } else if (!suppressLoader) {
          setLoading(false);
        }
      }
    },
    [pageSize, searchQuery, dateRange, showLoader, hideLoader, setLoading]
  );

  // Load trips when dependencies change
  useEffect(() => {
    loadTrips(currentPage);
  }, [currentPage, loadTrips]);

  // Cleanup function to abort any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSearch = useCallback((query: string) => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setSearchQuery(query);
      setCurrentPage(1);
    }, 500);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      // Cancel any pending request before changing page
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      // Cancel any pending request before changing page size
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setPageSize(Number(event.target.value));
      setCurrentPage(1);
    },
    []
  );

  const handleRefresh = useCallback(async () => {
    // Cancel any pending request before refresh
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    try {
      setLoading(true);
      await loadTrips(currentPage, true);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  }, [loadTrips, currentPage, setLoading]);

  const handleSetStartDate = useCallback((newStartDate: number) => {
    // Cancel any pending request before changing date
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setDateRange((prev) => ({
      ...prev,
      startDate: newStartDate,
    }));
  }, []);

  const handleSetEndDate = useCallback((newEndDate: number) => {
    // Cancel any pending request before changing date
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setDateRange((prev) => ({
      ...prev,
      endDate: newEndDate,
    }));
  }, []);

  const resetDates = useCallback(() => {
    // Cancel any pending request before resetting dates
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setDateRange({
      startDate: null,
      endDate: null,
    });
    if (dateTimePickerStartRef.current) dateTimePickerStartRef.current.reset();
    if (dateTimePickerEndRef.current) dateTimePickerEndRef.current.reset();
  }, []);

  // Memoized table data with null checks
  const tableData = useMemo(
    () =>
      trips?.map((trip: any) => ({
        jobCode: trip?.jobDetails?.jobCode || "N/A",
        jobBreakupDetailId: trip?.jobBreakupDetailId || "N/A",
        containerNumber: trip?.containerNumber || "N/A",
        containerSiTy: `${trip?.containerSize || "N/A"} ${
          trip?.containerType || "N/A"
        }`,
        tripStatus: trip?.tripStatus || "N/A",
        pickedAt: trip?.startDate
          ? moment(trip.startDate).format("DD/MM/YY hh:mm A")
          : "N/A",
        droppedAt: trip?.endDate
          ? moment(trip.endDate).format("DD/MM/YY hh:mm A")
          : "N/A",
        truckIdName: trip?.truckIdName?.name || "N/A",
        driverIdName: trip?.driverIdName?.name || "N/A",
        transporterIdName: trip?.transporterIdName?.name || "N/A",
      })) || [],
    [trips]
  );

  // Memoized columns definition
  const columns = useMemo(
    () => [
      // {
      //   header: "Job Code",
      //   accessor: "jobCode",
      //   // sortable: true,
      //   centerAlign: true,
      //   width: "100px",
      //   cell: (value: string) => value || "N/A",
      // },
      {
        header: "Trip Id.",
        accessor: "jobBreakupDetailId",
        // sortable: true,
        centerAlign: true,
        width: "100px",
        cell: (value: string) => value || "N/A",
      },
      {
        header: "Container No.",
        accessor: "containerNumber",
        uppercase: true,
        width: "130px",
        minWidth: "130px",
        cell: (value: string) => value || "N/A",
      },
      {
        header: "Size & Type",
        accessor: "containerSiTy",
        // sortable: true,
        centerAlign: true,
        width: "120px",
        minWidth: "120px",
        cell: (value: string) => {
          const displayValue = value || "N/A";
          if (displayValue === "N/A") return displayValue;
          const [size, type] = displayValue.split(" ");
          return <ContainerType size={size} type={type} />;
        },
      },

      {
        header: "Truck Number",
        accessor: "truckIdName",
        width: "150px",
        minWidth: "150px",
        cell: (value: string) => {
          const displayValue = value || "N/A";
          return displayValue !== "N/A"
            ? transformTruckNumber(displayValue)
            : displayValue;
        },
      },
      {
        header: "Driver Name",
        accessor: "driverIdName",
        cell: (value: string) => value || "N/A",
      },
      {
        header: "Transporter Name",
        accessor: "transporterIdName",
        cell: (value: string) => value || "N/A",
      },
      {
        header: "Picked at",
        accessor: "pickedAt",
        // sortable: true,
        cell: (value: string) => value || "N/A",
      },
      {
        header: "Dropped at",
        accessor: "droppedAt",
        // sortable: true,
        cell: (value: string) => value || "N/A",
      },
      {
        header: "Status",
        accessor: "tripStatus",
        // sortable: true,
        centerAlign: true,
        width: "120px",
        cell: (value: string, row: Record<string, any>) => {
          const containerData = {
            jobBreakupDetailId: row.jobBreakupDetailId,
            containerNumber: row.containerNumber,
            containerSize: row.containerSize,
            containerType: row.containerType,
            tripStatus: row.tripStatus,
            startDate: row.startDate,
            endDate: row.endDate,
            updatedDate: row.updatedDate,
            truckIdName: row.truckIdName,
            driverIdName: row.driverIdName,
            transporterIdName: row.transporterIdName,
            createdByUserId: 0,
            updatedByUserId: null,
            singleTripEtaInSeconds: null,
            priority: "LOW",
            createdDate: null,
          };
          return <ContainerStatus container={containerData} />;
        },
      },
    ],
    [] // Removed searchQuery dependency since we no longer use it for highlighting
  );

  return (
    <div className="p-[20px] trip-history-page">
      <StatsBoxContainer stats={statsData} />

      <div className="flex justify-between items-center py-[20px]">
        <div className="mt-2">
          <SearchBar
            placeholder="Search by Trip ID"
            onSearch={handleSearch}
            showClearButton={true}
          />
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2 ">
            <div className="form-group ">
              <label htmlFor="startDate">Start Date</label>
              <DateTimePickerComponent
                skipMinDate={true}
                ref={dateTimePickerStartRef}
                onChange={(newValue: number | null) => {
                  if (newValue) handleSetStartDate(newValue);
                }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date</label>
              <DateTimePickerComponent
                skipMinDate={true}
                ref={dateTimePickerEndRef}
                onChange={(newValue: number | null) => {
                  if (newValue) handleSetEndDate(newValue);
                }}
              />
            </div>

            <button
              type="button"
              className="secondary_button !mt-[5px] min-w-max"
              onClick={resetDates}
              disabled={
                dateRange.startDate === null && dateRange.endDate === null
              }
            >
              Reset Dates
            </button>

            <button
              className="secondary_button !mt-[5px] min-w-max"
              onClick={handleRefresh}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {isInitialLoading ? (
        <div className="center_floater_container max-h-[231px] text-[20px]">
          {/* Loading... */}
        </div>
      ) : trips?.length > 0 || totalElements > 0 ? (
        <div>
          <TableComponent
            columns={columns}
            data={tableData}
            showCheckboxes={false}
            showSerialNumbers={true}
            searchQuery={searchQuery}
            className="trip-history-table"
            showPagination={true}
            pageSize={pageSize}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            highlightSearchMatches={false}
            enableClientSideFiltering={false}
            totalElements={totalElements}
            isServerSidePagination={true}
          />
        </div>
      ) : (
        <div className="center_floater_container max-h-[231px] text-[20px]">
          No results !
        </div>
      )}
    </div>
  );
}
